// Layer 4 (UI) — the panel row's right column (polish.P20, comp A
// `waitingCard`): the OCR-01 override gate — the card's one nested box, the
// Advisory approval-gate recipe — then the BM01 stop as one compact row that
// links to its case. Renders `waitingOnYou()` only.

import { Link } from 'react-router-dom';
import { cn } from '@/shared/cn';
import { GATE_APPROVED_SURFACE, GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON, gatePillClass } from '../advisory/gate-ui';
import { DowntimeCard, SYN_CHIP } from '../downtime/DowntimeCard';
import { ToneChip } from '../process/ProcessUi';
import type { OverrideGateRead, StopRowRead, WaitingRead } from './WaitingOnYou.transform';
import { IdCode, OwnerChip, REF_LINK } from './risk-ui';

const CAPS_SM = 'text-[10px] font-semibold uppercase tracking-[0.07em] text-muted';

function GatePill({ gate }: { gate: OverrideGateRead }) {
  if (gate.status === 'approved') return <span className={gatePillClass('approved')} data-gate-pill="approved">{gate.pill}</span>;
  if (gate.status === 'declined') return <ToneChip tone="neutral" data-gate-pill="declined">{gate.pill}</ToneChip>;
  return <ToneChip tone="warn" data-gate-pill="pending">{gate.pill}</ToneChip>;
}

function OverrideGate({
  gate,
  onDecide,
}: {
  gate: OverrideGateRead;
  onDecide: (kind: 'approved' | 'declined') => void;
}) {
  return (
    <div
      data-approval-gate={gate.status === 'pending' ? 'draft' : gate.status}
      className={cn(
        'flex flex-col gap-1.5 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-3.5 py-3 transition-colors duration-300 motion-reduce:transition-none',
        gate.status === 'approved' && GATE_APPROVED_SURFACE,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-ink-primary">Override authority change</span>
        <span className="flex-1" />
        <GatePill gate={gate} />
      </div>
      <p className="text-xs leading-snug text-ink-secondary [text-wrap:pretty]">
        <IdCode className="mr-1">{gate.interlockId}</IdCode>
        {gate.subject}
      </p>
      <p className="flex flex-wrap items-baseline gap-2 text-[13px]">
        <span className="text-muted">{gate.fromAuthority}</span>
        <span aria-hidden className="text-muted">→</span>
        <span className="font-semibold text-ink-primary">{gate.toAuthority}</span>
      </p>
      <p className="text-xs leading-snug text-ink-secondary">{gate.reason}</p>
      <p className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
        <IdCode ink={false}>{gate.requestId}</IdCode>
        <span>· requested by</span>
        <OwnerChip>{gate.requestedBy}</OwnerChip>
        <span className="tabular-nums">{gate.requestedOn}</span>
        {SYN_CHIP}
      </p>
      {/* Comp A drops the whole `act` row once decided — the pill already names
          the Plant Manager — so the decider is named exactly once. */}
      {gate.status === 'pending' ? (
        <div className="mt-0.5 flex flex-wrap items-end gap-1.5">
          <span className="flex min-w-0 flex-col items-start gap-1 text-[11.5px] text-muted">
            Decides
            <OwnerChip>{gate.approver}</OwnerChip>
          </span>
          {gate.canDecide ? (
            // One nowrap group: the pair wraps to the next line together, in
            // order, rather than the Approve dropping alone below the Decline.
            <span className="ml-auto flex items-end gap-1.5 whitespace-nowrap">
              <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-8')} onClick={() => onDecide('declined')}>
                Decline
              </button>
              <button type="button" className={cn(GATE_PRIMARY_BUTTON, 'h-8')} onClick={() => onDecide('approved')} data-gate-approve>
                Approve
              </button>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StopRow({ stop, projectId }: { stop: StopRowRead; projectId: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2.5 gap-y-1 border-t border-[var(--color-divider)] pt-2.5" data-waiting-stop={stop.caseId}>
      <span className="text-[12.5px] font-semibold leading-snug text-ink-primary">{stop.title}</span>
      <ToneChip tone="neutral">{stop.due}</ToneChip>
      <span className="col-span-2 flex flex-wrap items-center gap-1.5">
        <span className={CAPS_SM}>Decides</span>
        <OwnerChip>{stop.decides}</OwnerChip>
        <span className={CAPS_SM}>Confirms</span>
        <OwnerChip>{stop.confirms}</OwnerChip>
      </span>
      <span className="col-span-2 flex flex-wrap items-center gap-2 text-xs">
        <IdCode ink={false}>{stop.refs}</IdCode>
        <Link to={`/projects/${projectId}/advisory/case/${stop.caseId}`} className={REF_LINK} data-case-link={stop.caseId}>
          Case {stop.caseCode} ↗
        </Link>
      </span>
    </div>
  );
}

export function WaitingOnYou({
  read,
  projectId,
  onDecide,
}: {
  read: WaitingRead;
  projectId: string;
  onDecide: (kind: 'approved' | 'declined') => void;
}) {
  return (
    <DowntimeCard
      title={read.title}
      actions={<ToneChip tone="neutral" data-waiting-count={String(read.count)}>{String(read.count)}</ToneChip>}
      className="flex h-full flex-col [&>header]:mb-2.5"
    >
      <div className="flex flex-col gap-2.5">
        {read.gate ? <OverrideGate gate={read.gate} onDecide={onDecide} /> : null}
        {read.stop ? <StopRow stop={read.stop} projectId={projectId} /> : null}
      </div>
    </DowntimeCard>
  );
}
